import { extractErrorMessage, suggestionName } from "../utils/acceptSuggestions";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { useSearchParams } from "react-router-dom";
import RecoveryReviewPage from "./RecoveryReviewPage";
import AIPowerSearch from "../components/AIPowerSearch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  FileText,
  Shield,
  Building2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Trash2,
  RefreshCw,
  Sparkles,
  CheckSquare,
  Lock,
  ShieldCheck,
  History,
  TrendingUp,
  Zap,
  Plus,
  HelpCircle,
  Info,
  Users,
  Target,
  Lightbulb,
  Cpu,
  ClipboardCheck,
  ListOrdered,
  Loader2,
  BarChart3,
  Wrench,
} from "lucide-react";
import { FormattedContent } from "../components/FormattedContent";
import { RecoveryGanttChart } from "../components/RecoveryGanttChart";
import AIRecommendationsButton from "../components/AIRecommendationsButton";
import ManualWorkaroundsTab from "../components/ManualWorkaroundsTab";
import StepDialog from "../components/StepDialog";
import {
  AIInlineSuggestion,
  AIAutoFillButton,
  StepSummaryCard,
} from "../components/WizardComponents";

const PLAN_TYPES = [
  { 
    value: "erp", 
    label: "Emergency Response Plan", 
    icon: Shield, 
    description: "Immediate response procedures for the first 72 hours",
    color: "bg-red-100 text-red-700 border-red-200",
    aiTip: "Best for incidents requiring immediate action: cyber attacks, natural disasters, facility emergencies.",
  },
  { 
    value: "bprp", 
    label: "Business Process Recovery Plan", 
    icon: TrendingUp, 
    description: "Process-specific recovery procedures and workarounds",
    color: "bg-blue-100 text-blue-700 border-blue-200",
    aiTip: "Ideal for recovering specific business processes like order processing, payroll, or customer service.",
  },
  { 
    value: "fcp", 
    label: "Facility Continuity Plan", 
    icon: Building2, 
    description: "Alternate site and remote work procedures",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    aiTip: "Essential for scenarios where your primary location is inaccessible.",
  },
  { 
    value: "it_drp", 
    label: "IT Disaster Recovery Plan", 
    icon: Cpu, 
    description: "IT systems, data backup, and technology recovery procedures",
    color: "bg-purple-100 text-purple-700 border-purple-200",
    aiTip: "Critical for IT systems recovery including servers, databases, networks, and applications. Defines RTO/RPO for technology assets.",
  },
];

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600",
  pending_review: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  published: "bg-sky-100 text-sky-700",
};

// Field tooltip component
function FieldTooltip({ content }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative inline-block ml-1">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="text-slate-400 hover:text-sky-700 transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-lg bottom-6 left-0">
            <div className="absolute w-2 h-2 bg-slate-900 transform rotate-45 -bottom-1 left-2" />
            {content}
          </div>
        </>
      )}
    </div>
  );
}

export default function RecoveryPlansPage() {
  const { user, api } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") === "overview" ? "overview" : "plans";
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [aiSearchResults, setAiSearchResults] = useState(null);
  // RP1a: recovery plans now source from approved Operational BIAs (process_bia),
  // not the legacy /bia/assessments model.
  const [processes, setProcesses] = useState([]);
  // AI-accept "draft & link" flow state
  const recoveryQueueRef = useRef({ items: [], created: 0, resolve: null });
  const [recoveryAcceptOpen, setRecoveryAcceptOpen] = useState(false);
  const [recoveryCurrent, setRecoveryCurrent] = useState(null);
  const [recoveryProcessId, setRecoveryProcessId] = useState("");
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [riskAssessments, setRiskAssessments] = useState([]);
  const [planStats, setPlanStats] = useState(null);
  const [optimizationQueue, setOptimizationQueue] = useState({ items: [], total: 0 });
  const [blackBoxStats, setBlackBoxStats] = useState(null);
  const [blackBoxLogs, setBlackBoxLogs] = useState([]);
  
  // Dialog states
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Wizard states
  const [wizardStep, setWizardStep] = useState(0);
  const [formData, setFormData] = useState({
    plan_type: "erp",
    plan_name: "",
    scope: "",
    selected_processes: [],
    selected_risks: [],
    include_gaps: true,
    include_recommendations: true,
    recovery_teams: [],
    notification_procedures: "",
    contact_stakeholder_ids: [],  // Link to Stakeholder Register for recovery team
  });
  const [tempTeam, setTempTeam] = useState("");
  
  // Recovery tasks state
  const [recoveryTasks, setRecoveryTasks] = useState([]);
  const [taskMode, setTaskMode] = useState(null); // null | "ai" | "blank"
  const [aiTasksLoading, setAiTasksLoading] = useState(false);
  const [editingTaskIdx, setEditingTaskIdx] = useState(null);
  const [taskViewMode, setTaskViewMode] = useState("table"); // "table" | "gantt"
  const [planDetailTab, setPlanDetailTab] = useState("overview"); // "overview" | "workarounds"
  
  // Stakeholder integration state
  const [stakeholders, setStakeholders] = useState([]);
  const [showStakeholderPicker, setShowStakeholderPicker] = useState(false);

  const isBCMAdmin = user?.role === "bcm_manager" || user?.role === "department_head" || user?.role === "admin" || user?.role === "it_drp_manager";
  // Edit is allowed for the same roles the backend PUT /plans/{id} accepts.
  const canEditPlans = ["process_manager", "it_function_manager", "department_head", "bcm_manager", "admin"].includes(user?.role);
  // Reject / send-back is Department Head or admin only (mirrors backend POST /plans/{id}/reject).
  const canRejectPlans = ["department_head", "admin"].includes(user?.role);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/plans");
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    }
  }, [api]);

  const fetchBIAAssessments = useCallback(async () => {}, []);  // RP1a: legacy /bia/assessments fetch removed

  const fetchProcesses = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/processes");
      setProcesses(response.data.processes || []);
    } catch (error) {
      console.error("Failed to fetch processes:", error);
    }
  }, [api]);

  const fetchRiskAssessments = useCallback(async () => {
    try {
      const response = await api.get("/bia/risks");
      setRiskAssessments(response.data || []);
    } catch (error) {
      console.error("Failed to fetch risk assessments:", error);
    }
  }, [api]);

  // ── AI-accept "draft & link": each suggestion links to a BIA process the admin picks ──
  const advanceRecoveryQueue = useCallback(() => {
    const q = recoveryQueueRef.current;
    if (!q.items.length) {
      setRecoveryAcceptOpen(false);
      setRecoveryCurrent(null);
      setRecoveryProcessId("");
      const { resolve, created } = q;
      recoveryQueueRef.current = { items: [], created: 0, resolve: null };
      if (resolve) resolve(created > 0 ? { created } : { created: 0, error: "No plan created" });
      return;
    }
    setRecoveryCurrent(q.items[0]);
    setRecoveryProcessId("");
    setRecoveryAcceptOpen(true);
  }, []);

  const handleAcceptRecoverySuggestions = useCallback((items) => new Promise((resolve) => {
    recoveryQueueRef.current = { items: [...(items || [])], created: 0, resolve };
    advanceRecoveryQueue();
  }), [advanceRecoveryQueue]);

  const confirmRecoveryAccept = async () => {
    if (!recoveryProcessId) { toast.error("Select a BIA process to link this plan to"); return; }
    const o = recoveryCurrent && typeof recoveryCurrent === "object" ? recoveryCurrent : {};
    setRecoveryBusy(true);
    try {
      await api.post("/bia-engine/plans/create-from-suggestion", {
        plan_name: o.plan_name || suggestionName(recoveryCurrent),
        strategy: o.strategy || null,
        rto_target: o.rto_target || null,
        regulatory_driver: o.regulatory_driver || null,
        process_id: recoveryProcessId,
      });
      recoveryQueueRef.current.created += 1;
      recoveryQueueRef.current.items = recoveryQueueRef.current.items.slice(1);
      toast.success("Draft recovery plan created");
      await Promise.all([fetchPlans(), fetchPlanStats()]);
      setRecoveryBusy(false);
      advanceRecoveryQueue();
    } catch (e) {
      setRecoveryBusy(false);
      toast.error(extractErrorMessage(e, "Failed to create recovery plan"));
    }
  };

  const skipRecoveryAccept = () => {
    recoveryQueueRef.current.items = recoveryQueueRef.current.items.slice(1);
    advanceRecoveryQueue();
  };

  const cancelRecoveryAccept = () => {
    recoveryQueueRef.current.items = [];
    advanceRecoveryQueue();
  };

  const fetchPlanStats = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/plans/stats/summary");
      setPlanStats(response.data);
    } catch (error) {
      console.error("Failed to fetch plan stats:", error);
    }
  }, [api]);

  const fetchOptimizationQueue = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/optimization-queue");
      setOptimizationQueue(response.data);
    } catch (error) {
      console.error("Failed to fetch optimization queue:", error);
    }
  }, [api]);

  // Fetch stakeholders for recovery team selection
  const fetchStakeholders = useCallback(async () => {
    try {
      const response = await api.get("/stakeholders/for-recovery-team");
      if (response.data.success) {
        setStakeholders(response.data.data.stakeholders || []);
      }
    } catch (error) {
      console.error("Failed to fetch stakeholders:", error);
      setStakeholders([]);
    }
  }, [api]);

  const fetchBlackBoxStats = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/black-box/stats");
      setBlackBoxStats(response.data);
    } catch (error) {
      console.error("Failed to fetch black box stats:", error);
    }
  }, [api]);

  const fetchBlackBoxLogs = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/black-box/logs?days=30");
      setBlackBoxLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch black box logs:", error);
    }
  }, [api]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchPlans(),
        fetchBIAAssessments(),
        fetchProcesses(),
        fetchRiskAssessments(),
        fetchPlanStats(),
        fetchOptimizationQueue(),
        fetchBlackBoxStats(),
        fetchBlackBoxLogs(),
        fetchStakeholders(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchPlans, fetchBIAAssessments, fetchProcesses, fetchRiskAssessments, fetchPlanStats, fetchOptimizationQueue, fetchBlackBoxStats, fetchBlackBoxLogs, fetchStakeholders]);

  // Auto-open wizard from demo walkthrough
  useEffect(() => {
    if (searchParams.get("autoWizard") === "true" && !loading) {
      const timer = setTimeout(() => {
        resetForm();
        setShowGenerateDialog(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [searchParams, loading]);

  const resetForm = () => {
    setFormData({
      plan_type: "erp",
      plan_name: "",
      scope: "",
      selected_processes: [],
      selected_risks: [],
      include_gaps: true,
      include_recommendations: true,
      recovery_teams: [],
      notification_procedures: "",
      contact_stakeholder_ids: [],
    });
    setTempTeam("");
    setWizardStep(0);
    setShowStakeholderPicker(false);
    setRecoveryTasks([]);
    setTaskMode(null);
    setEditingTaskIdx(null);
  };

  const handleGeneratePlan = async () => {
    setGenerating(true);
    try {
      const response = await api.post("/bia-engine/plans/generate", {
        plan_type: formData.plan_type,
        include_gaps: formData.include_gaps,
        include_recommendations: formData.include_recommendations,
        custom_name: formData.plan_name || undefined,
        scope: formData.scope || undefined,
        process_ids: formData.selected_processes.length > 0 ? formData.selected_processes : undefined,
        selected_risks: formData.selected_risks.length > 0 ? formData.selected_risks : undefined,
        recovery_tasks: recoveryTasks.length > 0 ? recoveryTasks : undefined,
      });
      toast.success(`${response.data.title} generated successfully!`);
      setShowGenerateDialog(false);
      resetForm();
      await Promise.all([fetchPlans(), fetchPlanStats(), fetchBlackBoxLogs()]);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to generate plan"));
    } finally {
      setGenerating(false);
    }
  };

  const handleViewPlan = async (plan) => {
    try {
      const response = await api.get(`/bia-engine/plans/${plan.id}`);
      setSelectedPlan(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error("Failed to load plan");
    }
  };

  const handleApprovePlan = async (planId) => {
    try {
      await api.post(`/bia-engine/plans/${planId}/approve`);
      toast.success("Plan approved and signed off!");
      await Promise.all([fetchPlans(), fetchPlanStats(), fetchBlackBoxLogs()]);
      if (selectedPlan?.id === planId) {
        const response = await api.get(`/bia-engine/plans/${planId}`);
        setSelectedPlan(response.data);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to approve plan"));
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    
    try {
      await api.delete(`/bia-engine/plans/${planId}`);
      toast.success("Plan deleted");
      await Promise.all([fetchPlans(), fetchPlanStats()]);
    } catch (error) {
      toast.error("Failed to delete plan");
    }
  };

  // ── Edit plan (draft / pending_review only) ──
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", objective: "", rto_hours: "", rpo_hours: "", content: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditPlan = async (plan) => {
    try {
      const response = await api.get(`/bia-engine/plans/${plan.id}`);
      const p = response.data;
      setEditPlan(p);
      setEditForm({
        title: p.title || "",
        description: p.description || "",
        objective: p.objective || "",
        rto_hours: p.rto_hours ?? "",
        rpo_hours: p.rpo_hours ?? "",
        content: p.content || "",
      });
      setShowEditDialog(true);
    } catch (e) {
      toast.error("Failed to load plan for editing");
    }
  };

  const handleSaveEdit = async () => {
    if (!editPlan) return;
    if (!editForm.title.trim()) { toast.error("Title is required"); return; }
    setSavingEdit(true);
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        objective: editForm.objective,
        content: editForm.content,
      };
      if (editForm.rto_hours !== "" && !isNaN(Number(editForm.rto_hours))) payload.rto_hours = Number(editForm.rto_hours);
      if (editForm.rpo_hours !== "" && !isNaN(Number(editForm.rpo_hours))) payload.rpo_hours = Number(editForm.rpo_hours);
      await api.put(`/bia-engine/plans/${editPlan.id}`, payload);
      toast.success("Recovery plan updated");
      setShowEditDialog(false);
      setEditPlan(null);
      await Promise.all([fetchPlans(), fetchPlanStats()]);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to update plan"));
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Reject plan (send back for corrections) — DH / admin only ──
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectPlan, setRejectPlan] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingBusy, setRejectingBusy] = useState(false);

  const openRejectPlan = (plan) => {
    setRejectPlan(plan);
    setRejectReason("");
    setShowRejectDialog(true);
  };

  const handleRejectPlan = async () => {
    if (!rejectPlan) return;
    if (!rejectReason.trim()) { toast.error("Please provide a reason for sending this plan back"); return; }
    setRejectingBusy(true);
    try {
      await api.post(`/bia-engine/plans/${rejectPlan.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Plan sent back to draft for corrections");
      setShowRejectDialog(false);
      setRejectPlan(null);
      await Promise.all([fetchPlans(), fetchPlanStats(), fetchBlackBoxLogs()]);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to reject plan"));
    } finally {
      setRejectingBusy(false);
    }
  };

  const handleApproveOptimization = async (itemId) => {
    try {
      await api.post(`/bia-engine/optimization-queue/${itemId}/approve`);
      toast.success("Optimization applied!");
      await fetchOptimizationQueue();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to apply optimization"));
    }
  };

  const toggleProcess = (processId) => {
    setFormData(prev => ({
      ...prev,
      selected_processes: prev.selected_processes.includes(processId)
        ? prev.selected_processes.filter(id => id !== processId)
        : [...prev.selected_processes, processId]
    }));
  };

  const toggleRisk = (riskId) => {
    setFormData(prev => ({
      ...prev,
      selected_risks: prev.selected_risks.includes(riskId)
        ? prev.selected_risks.filter(id => id !== riskId)
        : [...prev.selected_risks, riskId]
    }));
  };

  const addTeam = () => {
    if (tempTeam.trim() && !formData.recovery_teams.includes(tempTeam.trim())) {
      setFormData(prev => ({ ...prev, recovery_teams: [...prev.recovery_teams, tempTeam.trim()] }));
      setTempTeam("");
    }
  };

  const removeTeam = (index) => {
    setFormData(prev => ({
      ...prev,
      recovery_teams: prev.recovery_teams.filter((_, i) => i !== index)
    }));
  };

  // ── Recovery Task Handlers ──
  const handleAIGenerateTasks = async () => {
    setAiTasksLoading(true);
    try {
      const approvedSel = processes.filter(p => p.status === "approved" && formData.selected_processes.includes(p.id));
      const processNames = approvedSel.map(p => p.process_name);
      const riskNames = riskAssessments
        .filter(r => formData.selected_risks.includes(r.id))
        .map(r => r.title);

      const response = await api.post("/bia-engine/plans/generate-tasks", {
        plan_type: formData.plan_type,
        org_context: formData.scope || "",
        process_names: processNames,
        risk_names: riskNames,
        process_ids: formData.selected_processes,
      });
      setRecoveryTasks(response.data.tasks || []);
      setTaskMode("ai");
      toast.success(response.data.source === "ai" ? "AI-generated tasks ready for review!" : "Template tasks loaded — customize as needed.");
    } catch (error) {
      toast.error("Failed to generate tasks");
    } finally {
      setAiTasksLoading(false);
    }
  };

  const handleStartBlankTemplate = () => {
    setRecoveryTasks([
      { task_id: "T001", actual_task: "", task_owner: "", predecessor_task_id: "", estimated_duration: "", start_time: "", completion_time: "", notes: "" },
    ]);
    setTaskMode("blank");
    setEditingTaskIdx(0);
  };

  const addTask = () => {
    const nextId = `T${String(recoveryTasks.length + 1).padStart(3, "0")}`;
    const lastTaskId = recoveryTasks.length > 0 ? recoveryTasks[recoveryTasks.length - 1].task_id : "";
    setRecoveryTasks(prev => [...prev, {
      task_id: nextId, actual_task: "", task_owner: "", predecessor_task_id: lastTaskId,
      estimated_duration: "", start_time: "", completion_time: "", notes: ""
    }]);
    setEditingTaskIdx(recoveryTasks.length);
  };

  const removeTask = (index) => {
    setRecoveryTasks(prev => prev.filter((_, i) => i !== index));
    setEditingTaskIdx(null);
  };

  const updateTask = (index, field, value) => {
    setRecoveryTasks(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const getPlanTypeConfig = (planType) => {
    return PLAN_TYPES.find(pt => pt.value === planType) || PLAN_TYPES[0];
  };

  const wizardSteps = [
    { id: "plan_type", title: "Plan Type", subtitle: "Select the type of recovery plan to generate", isComplete: !!formData.plan_type },
    { id: "scope", title: "Scope & Processes", subtitle: "Define what this plan covers", isComplete: formData.selected_processes.length > 0 || !!formData.scope },
    { id: "risk", title: "Risk Integration", subtitle: "Link risk assessments and build your team", isComplete: formData.selected_risks.length > 0 || formData.recovery_teams.length > 0 },
    { id: "tasks", title: "Recovery Tasks", subtitle: "Build the task checklist for this plan", isComplete: recoveryTasks.length > 0 },
    { id: "review", title: "Review & Generate", subtitle: "Confirm and create your recovery plan", isComplete: false },
  ];

  // Business Users can view plans but not create/edit
  const canCreatePlans = user?.role === "bcm_manager" || user?.role === "admin";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="plans-loading">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recovery-plans-page">
      {isBCMAdmin ? (
        <AISuggestionsBanner module="recovery_plans"
          onAccept={handleAcceptRecoverySuggestions}
          existingKeys={plans.map((p) => p.title)}
          itemKeyOf={(raw) => (raw?.plan_name || raw?.name || String(raw ?? ""))} />
      ) : (
        <AISuggestionsBanner module="recovery_plans" />
      )}

      {/* AI-accept: link a suggested plan to a BIA process before creating a draft */}
      <Dialog open={recoveryAcceptOpen} onOpenChange={(o) => { if (!o) cancelRecoveryAccept(); }}>
        <DialogContent data-testid="recovery-accept-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-sky-600" />
              Link recovery plan to a BIA process
            </DialogTitle>
            <DialogDescription>
              AI will draft "{recoveryCurrent?.plan_name || suggestionName(recoveryCurrent)}" as a
              draft plan seeded from your organization profile. Choose the business process it protects.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {recoveryCurrent?.strategy && (
              <p className="text-sm text-slate-600"><span className="font-medium">Strategy:</span> {recoveryCurrent.strategy}</p>
            )}
            {recoveryCurrent?.rto_target && (
              <p className="text-sm text-slate-600"><span className="font-medium">RTO target:</span> {recoveryCurrent.rto_target}</p>
            )}
            <div className="space-y-1.5">
              <Label>BIA Process *</Label>
              <Select value={recoveryProcessId} onValueChange={setRecoveryProcessId}>
                <SelectTrigger data-testid="recovery-accept-process-select">
                  <SelectValue placeholder="Select a BIA process..." />
                </SelectTrigger>
                <SelectContent>
                  {processes.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.process_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {processes.length === 0 && (
                <p className="text-xs text-amber-600">No BIA processes found — create a BIA process first.</p>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={cancelRecoveryAccept} disabled={recoveryBusy} data-testid="recovery-accept-cancel">Cancel</Button>
            <Button variant="outline" onClick={skipRecoveryAccept} disabled={recoveryBusy} data-testid="recovery-accept-skip">Skip</Button>
            <Button onClick={confirmRecoveryAccept} disabled={recoveryBusy || !recoveryProcessId} data-testid="recovery-accept-confirm">
              {recoveryBusy ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {recoveryBusy ? "Creating…" : "Create Draft Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-100 text-emerald-700">DO Phase</Badge>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">ISO 22301:8.4</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Recovery Plans</h1>
          <p className="text-slate-600">
            {canCreatePlans 
              ? "Generate AI-powered BC plans and procedures"
              : "View organization's recovery plans and procedures"}
          </p>
        </div>
        {canCreatePlans && (
          <div className="flex gap-2">
            <AIRecommendationsButton 
              module="recovery_plan" 
              contextData={{
                total_plans: planStats?.total_plans || 0,
                approved_plans: planStats?.approved_plans || 0,
                pending_plans: planStats?.pending_plans || 0
              }}
              api={api}
            />
            <Button 
              onClick={() => setShowGenerateDialog(true)} 
              className="bg-sky-700 hover:bg-sky-800" 
              data-testid="generate-plan-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate New Plan
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Plans</p>
                <p className="text-2xl font-bold text-slate-900">{planStats?.total_plans || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-sky-700 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Approved</p>
                <p className="text-2xl font-bold text-emerald-600">{planStats?.approved_plans || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className={optimizationQueue.pending_approval > 0 ? "border-amber-200" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Optimization Items</p>
                <p className="text-2xl font-bold text-slate-900">{optimizationQueue.pending_approval || 0}</p>
              </div>
              <AlertTriangle className={`w-8 h-8 opacity-50 ${optimizationQueue.pending_approval > 0 ? "text-amber-500" : "text-slate-400"}`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Black Box Entries</p>
                <p className="text-2xl font-bold text-slate-900">{blackBoxStats?.total_entries || 0}</p>
              </div>
              <Lock className="w-8 h-8 text-slate-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue={defaultTab} className="space-y-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="plans" className="data-[state=active]:bg-sky-50">
            <FileText className="w-4 h-4 mr-2" />
            Recovery Plans
          </TabsTrigger>
          <TabsTrigger value="optimization" className="data-[state=active]:bg-sky-50">
            <Zap className="w-4 h-4 mr-2" />
            Optimization Queue
            {optimizationQueue.pending_approval > 0 && (
              <Badge variant="destructive" className="ml-2">{optimizationQueue.pending_approval}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="blackbox" className="data-[state=active]:bg-sky-50">
            <Lock className="w-4 h-4 mr-2" />
            Black Box Audit
          </TabsTrigger>
          <TabsTrigger value="template" className="data-[state=active]:bg-sky-50" data-testid="plan-template-tab">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Plan Template
          </TabsTrigger>
          <TabsTrigger value="overview" className="data-[state=active]:bg-sky-50" data-testid="task-overview-tab">
            <Eye className="w-4 h-4 mr-2" />
            Task Overview
          </TabsTrigger>
        </TabsList>

        {/* Recovery Plans Tab */}
        <TabsContent value="plans" data-testid="plans-tab">
          <div className="mb-4">
            <AIPowerSearch
              module="recovery_plans"
              onResults={(results) => setAiSearchResults(results)}
              onClear={() => setAiSearchResults(null)}
              placeholder='Try: "find all approved plans" or "IT disaster recovery plans"...'
            />
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-400" />
                Generated Recovery Plans
              </CardTitle>
              <CardDescription>
                AI-generated plans based on your BIA and risk assessments
              </CardDescription>
            </CardHeader>
            <CardContent>
              {plans.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">
                    {canCreatePlans ? "No recovery plans generated yet" : "No recovery plans available to view"}
                  </p>
                  {canCreatePlans && (
                    <Button variant="outline" onClick={() => setShowGenerateDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Generate First Plan
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(aiSearchResults || plans).map((plan) => {
                    const config = getPlanTypeConfig(plan.plan_type);
                    const Icon = config.icon;
                    return (
                      <Card key={plan.id} className={`border-2 ${config.color.split(" ")[0]} hover:shadow-md transition-shadow`} data-testid={`plan-card-${plan.id}`}>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className={`p-2 rounded-lg ${config.color}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <Badge className={STATUS_COLORS[plan.status] || STATUS_COLORS.draft}>
                              {plan.status.replace("_", " ")}
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-slate-900 mb-1">{plan.title}</h3>
                          <p className="text-sm text-slate-500 mb-3">
                            {plan.processes_count} processes • {plan.risks_count || 0} risks • {plan.gaps_count} gaps{plan.tasks_count > 0 ? ` • ${plan.tasks_count} tasks` : ""}
                          </p>
                          <p className="text-xs text-slate-400 mb-4">
                            Generated: {new Date(plan.generated_at).toLocaleDateString()}
                          </p>
                          {/* Department Head Approvers */}
                          {plan.department_head_approvers?.length > 0 && (
                            <div className="mb-3 flex items-center gap-1 flex-wrap">
                              <Shield className="w-3 h-3 text-indigo-500" />
                              {plan.department_head_approvers.map((dh, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700">{dh.name}</Badge>
                              ))}
                              {plan.department_head_approved_by ? (
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700">DH Approved</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700">DH Pending</Badge>
                              )}
                            </div>
                          )}
                          <div className="flex gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => handleViewPlan(plan)}>
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                            {canCreatePlans && plan.status === "draft" && (
                              <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleApprovePlan(plan.id)}>
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Approve
                              </Button>
                            )}
                            {canEditPlans && (plan.status === "draft" || plan.status === "pending_review") && (
                              <Button size="sm" variant="outline" className="text-sky-700" onClick={() => openEditPlan(plan)} data-testid={`edit-plan-${plan.id}`}>
                                <Wrench className="w-3 h-3 mr-1" />
                                Edit
                              </Button>
                            )}
                            {canRejectPlans && (plan.status === "first_approved" || plan.status === "pending_review") && (
                              <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" onClick={() => openRejectPlan(plan)} data-testid={`reject-plan-${plan.id}`}>
                                <History className="w-3 h-3 mr-1" />
                                Send Back
                              </Button>
                            )}
                            {plan.department_head_approvers?.length > 0 && !plan.department_head_approved_by && plan.status !== "draft" && (
                              <Button size="sm" variant="outline" className="text-indigo-600" onClick={async () => {
                                try {
                                  await api.post(`/bia-engine/plans/${plan.id}/department-head-approve`);
                                  toast.success("Department Head approval recorded");
                                  fetchPlans();
                                } catch (err) { toast.error(extractErrorMessage(err, "Approval failed")); }
                              }} data-testid={`dept-head-approve-${plan.id}`}>
                                <Shield className="w-3 h-3 mr-1" />
                                DH Approve
                              </Button>
                            )}
                            {canCreatePlans && (
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeletePlan(plan.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Optimization Queue Tab */}
        <TabsContent value="optimization" data-testid="optimization-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Optimization Queue
              </CardTitle>
              <CardDescription>
                Identified gaps and mismatches requiring correction
              </CardDescription>
            </CardHeader>
            <CardContent>
              {optimizationQueue.items.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                  <p className="text-slate-500">No optimization items pending</p>
                  <p className="text-sm text-slate-400">Your BIA configuration is fully optimized</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Process</TableHead>
                      <TableHead>Current</TableHead>
                      <TableHead>Recommended</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {optimizationQueue.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {item.item_type.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{item.process_name}</TableCell>
                        <TableCell>{item.current_value}</TableCell>
                        <TableCell className="text-emerald-600 font-medium">{item.recommended_value}</TableCell>
                        <TableCell>
                          <Badge variant={item.severity === "high" || item.severity === "critical" ? "destructive" : "warning"}>
                            {item.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.approved ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Resolved</Badge>
                          ) : (
                            <Button size="sm" onClick={() => handleApproveOptimization(item.id)} data-testid={`approve-opt-${item.id}`}>
                              <CheckSquare className="w-3 h-3 mr-1" />
                              Apply
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Black Box Audit Tab */}
        <TabsContent value="blackbox" data-testid="blackbox-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-slate-600" />
                Black Box Audit Trail
              </CardTitle>
              <CardDescription>
                Immutable, timestamped log of all approvals and sign-offs for legal defensibility
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Stats Summary */}
              {blackBoxStats && (
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Plan Approvals</p>
                    <p className="text-xl font-bold text-slate-900">{blackBoxStats.by_event_type?.plan_approval || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Gap Corrections</p>
                    <p className="text-xl font-bold text-slate-900">{blackBoxStats.by_event_type?.gap_correction || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Sign-offs</p>
                    <p className="text-xl font-bold text-slate-900">{blackBoxStats.by_event_type?.sign_off || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Total Events</p>
                    <p className="text-xl font-bold text-slate-900">{blackBoxStats.total_entries || 0}</p>
                  </div>
                </div>
              )}

              {blackBoxLogs.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No audit entries yet</p>
                  <p className="text-sm text-slate-400">Approvals and sign-offs will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Integrity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blackBoxLogs.slice(0, 20).map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3 text-slate-400" />
                            {new Date(log.timestamp).toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {log.event_type.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{log.user_email}</TableCell>
                        <TableCell className="text-sm max-w-xs truncate">{log.action}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-emerald-600">
                            <ShieldCheck className="w-4 h-4" />
                            <span className="text-xs">Verified</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plan Template Tab */}
        <TabsContent value="template" data-testid="plan-template-tab-content">
          <div className="space-y-6">
            {/* Template Header */}
            <Card className="border-sky-200 bg-gradient-to-br from-indigo-50/50 to-slate-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ClipboardCheck className="w-5 h-5 text-sky-700" />
                  Standard Recovery Plan Template
                </CardTitle>
                <CardDescription>
                  This is the standard DRP/BCP task template used across all recovery plans. Every plan — whether BCP, IT-DRP, or Emergency Response — follows this structure for consistency.
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Template Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Recovery Plan — Task Table</CardTitle>
                <CardDescription>
                  Each recovery plan consists of ordered tasks with dependency chains. Start Time and Completion Time are recorded during actual exercises or incidents.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-800">
                        <TableHead className="text-white font-semibold w-20">Task ID</TableHead>
                        <TableHead className="text-white font-semibold">Actual Task</TableHead>
                        <TableHead className="text-white font-semibold w-36">Task Owner</TableHead>
                        <TableHead className="text-white font-semibold w-32">Predecessor Task ID</TableHead>
                        <TableHead className="text-white font-semibold w-32">Estimated Duration</TableHead>
                        <TableHead className="text-white font-semibold w-28">Start Time</TableHead>
                        <TableHead className="text-white font-semibold w-32">Completion Time</TableHead>
                        <TableHead className="text-white font-semibold w-40">Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="bg-blue-50/50" data-testid="template-sample-row-1">
                        <TableCell className="font-mono font-bold text-purple-700">T001</TableCell>
                        <TableCell className="font-medium">Activate emergency communication tree</TableCell>
                        <TableCell>Incident Commander</TableCell>
                        <TableCell className="font-mono text-slate-500">—</TableCell>
                        <TableCell>15 mins</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-sm text-slate-500">First action upon declaration</TableCell>
                      </TableRow>
                      <TableRow data-testid="template-sample-row-2">
                        <TableCell className="font-mono font-bold text-purple-700">T002</TableCell>
                        <TableCell className="font-medium">Assess damage to primary data center</TableCell>
                        <TableCell>IT-DRP Manager</TableCell>
                        <TableCell className="font-mono text-slate-500">T001</TableCell>
                        <TableCell>1 hour</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-sm text-slate-500">Document findings in incident log</TableCell>
                      </TableRow>
                      <TableRow className="bg-blue-50/50" data-testid="template-sample-row-3">
                        <TableCell className="font-mono font-bold text-purple-700">T003</TableCell>
                        <TableCell className="font-medium">Initiate failover to DR site</TableCell>
                        <TableCell>DevOps Team</TableCell>
                        <TableCell className="font-mono text-slate-500">T002</TableCell>
                        <TableCell>45 mins</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-sm text-slate-500">Use runbook DR-FAILOVER-01</TableCell>
                      </TableRow>
                      <TableRow data-testid="template-sample-row-4">
                        <TableCell className="font-mono font-bold text-purple-700">T004</TableCell>
                        <TableCell className="font-medium">Restore database from latest backup</TableCell>
                        <TableCell>DBA Team</TableCell>
                        <TableCell className="font-mono text-slate-500">T003</TableCell>
                        <TableCell>2 hours</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-sm text-slate-500">Verify RPO compliance</TableCell>
                      </TableRow>
                      <TableRow className="bg-blue-50/50" data-testid="template-sample-row-5">
                        <TableCell className="font-mono font-bold text-purple-700">T005</TableCell>
                        <TableCell className="font-medium">Run smoke tests and validate services</TableCell>
                        <TableCell>QA Lead</TableCell>
                        <TableCell className="font-mono text-slate-500">T004</TableCell>
                        <TableCell>30 mins</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-sm text-slate-500">Use validation checklist VC-001</TableCell>
                      </TableRow>
                      <TableRow data-testid="template-sample-row-6">
                        <TableCell className="font-mono font-bold text-purple-700">T006</TableCell>
                        <TableCell className="font-medium">Notify stakeholders of recovery status</TableCell>
                        <TableCell>Communications Lead</TableCell>
                        <TableCell className="font-mono text-slate-500">T005</TableCell>
                        <TableCell>15 mins</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-slate-400 italic text-sm">During exercise</TableCell>
                        <TableCell className="text-sm text-slate-500">Use template COMM-RESTORE</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Column Definitions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Column Definitions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                  <div className="flex gap-3">
                    <Badge variant="outline" className="shrink-0 font-mono h-6">Task ID</Badge>
                    <p className="text-sm text-slate-600">Unique identifier (T001, T002...). Auto-generated sequentially.</p>
                  </div>
                  <div className="flex gap-3">
                    <Badge variant="outline" className="shrink-0 font-mono h-6">Actual Task</Badge>
                    <p className="text-sm text-slate-600">Description of the specific recovery action to be performed.</p>
                  </div>
                  <div className="flex gap-3">
                    <Badge variant="outline" className="shrink-0 font-mono h-6">Task Owner</Badge>
                    <p className="text-sm text-slate-600">Person or team responsible for executing this task.</p>
                  </div>
                  <div className="flex gap-3">
                    <Badge variant="outline" className="shrink-0 font-mono h-6">Predecessor</Badge>
                    <p className="text-sm text-slate-600">Task ID that must complete before this task can begin. Creates the dependency chain.</p>
                  </div>
                  <div className="flex gap-3">
                    <Badge variant="outline" className="shrink-0 font-mono h-6">Est. Duration</Badge>
                    <p className="text-sm text-slate-600">Expected time to complete this task (used for RTO planning).</p>
                  </div>
                  <div className="flex gap-3">
                    <Badge variant="outline" className="shrink-0 font-mono h-6">Start Time</Badge>
                    <p className="text-sm text-slate-600">Actual start time — recorded during exercises or real incidents.</p>
                  </div>
                  <div className="flex gap-3">
                    <Badge variant="outline" className="shrink-0 font-mono h-6">Completion</Badge>
                    <p className="text-sm text-slate-600">Actual completion time — recorded during exercises or real incidents.</p>
                  </div>
                  <div className="flex gap-3">
                    <Badge variant="outline" className="shrink-0 font-mono h-6">Notes</Badge>
                    <p className="text-sm text-slate-600">Additional context, references, or instructions for the task owner.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Where This Template Is Used */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Where This Template Is Used</CardTitle>
                <CardDescription>This standard format appears across the following workflows:</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-50 border border-purple-100">
                    <Cpu className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-purple-900">IT-DRP Plan Wizard</p>
                      <p className="text-xs text-purple-700">Step 4 "Recovery Tasks" — define task-based IT disaster recovery procedures</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                    <TrendingUp className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-900">Recovery Package Wizard</p>
                      <p className="text-xs text-emerald-700">Step 4 "Application Recovery" — for cloud/DevOps-managed processes</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <Target className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900">My Recovery Tasks</p>
                      <p className="text-xs text-blue-700">Teams define their recovery plan using this format when submitting tasks</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900">Admin Review Pages</p>
                      <p className="text-xs text-amber-700">BCM Managers see this table when reviewing submitted recovery tasks and packages</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Task Overview Tab (merged from Plan & Task Overview) */}
        <TabsContent value="overview" data-testid="task-overview-tab-content">
          <RecoveryReviewPage embedded />
        </TabsContent>
      </Tabs>

      {/* Edit Recovery Plan dialog (draft / pending_review only) */}
      <Dialog open={showEditDialog} onOpenChange={(o) => { if (!o) { setShowEditDialog(false); setEditPlan(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-sky-700" />
              Edit Recovery Plan
            </DialogTitle>
            <DialogDescription>
              Update this plan while it is still in draft or pending review. Approved / published plans are locked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Title *</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-plan-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Objective</Label>
              <Input value={editForm.objective} onChange={(e) => setEditForm({ ...editForm, objective: e.target.value })} data-testid="edit-plan-objective" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>RTO (hours)</Label>
                <Input type="number" value={editForm.rto_hours} onChange={(e) => setEditForm({ ...editForm, rto_hours: e.target.value })} data-testid="edit-plan-rto" />
              </div>
              <div className="space-y-1.5">
                <Label>RPO (hours)</Label>
                <Input type="number" value={editForm.rpo_hours} onChange={(e) => setEditForm({ ...editForm, rpo_hours: e.target.value })} data-testid="edit-plan-rpo" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={2} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} data-testid="edit-plan-description" />
            </div>
            <div className="space-y-1.5">
              <Label>Plan Content</Label>
              <Textarea rows={8} value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} data-testid="edit-plan-content" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setShowEditDialog(false); setEditPlan(null); }} disabled={savingEdit} data-testid="edit-plan-cancel">Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit || !editForm.title.trim()} className="bg-sky-700 hover:bg-sky-800" data-testid="edit-plan-save">
              {savingEdit ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {savingEdit ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject / Send-back dialog (Department Head / admin only) */}
      <Dialog open={showRejectDialog} onOpenChange={(o) => { if (!o) { setShowRejectDialog(false); setRejectPlan(null); } }}>
        <DialogContent data-testid="reject-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" />
              Send Plan Back for Corrections
            </DialogTitle>
            <DialogDescription>
              "{rejectPlan?.title}" will be returned to <span className="font-medium">draft</span> so the author can make changes.
              Approval history is preserved in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Reason for sending back *</Label>
            <Textarea
              rows={3}
              placeholder="e.g., Recovery steps incomplete — please add the database failover procedure."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-plan-reason"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setShowRejectDialog(false); setRejectPlan(null); }} disabled={rejectingBusy} data-testid="reject-plan-cancel">Cancel</Button>
            <Button onClick={handleRejectPlan} disabled={rejectingBusy || !rejectReason.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="reject-plan-confirm">
              {rejectingBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <History className="w-4 h-4 mr-1" />}
              {rejectingBusy ? "Sending…" : "Send Back to Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StepDialog
        open={showGenerateDialog}
        onOpenChange={(open) => { setShowGenerateDialog(open); if (!open) resetForm(); }}
        title="Generate Recovery Plan"
        steps={wizardSteps}
        currentStep={wizardStep}
        onStepChange={setWizardStep}
        onSubmit={handleGeneratePlan}
        onCancel={() => { setShowGenerateDialog(false); resetForm(); }}
        submitLabel={generating ? "Generating..." : "Generate Plan"}
        submitDisabled={generating}
        saving={generating}
        canAdvance={
          wizardStep === 0 ? !!formData.plan_type :
          true
        }
        maxWidth="max-w-3xl"
      >
        {/* Step 0: Plan Type */}
        {wizardStep === 0 && (
          <div className="space-y-4">
            <AIInlineSuggestion
              type="tip"
              suggestion="Choose the plan type based on your primary recovery objective. ERP for immediate response, BPRP for process recovery, FCP for facility issues."
            />

                <div className="space-y-3">
                  <Label>Select Plan Type *</Label>
                  {PLAN_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.plan_type === type.value;
                    return (
                      <div
                        key={type.value}
                        onClick={() => setFormData({ ...formData, plan_type: type.value })}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected ? `${type.color} border-current` : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`plan-type-${type.value}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isSelected ? "bg-white/50" : "bg-slate-100"}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold">{type.label}</p>
                            <p className="text-sm opacity-75">{type.description}</p>
                          </div>
                          {isSelected && <CheckCircle2 className="w-5 h-5" />}
                        </div>
                        {isSelected && type.aiTip && (
                          <div className="mt-3 p-2 bg-white/50 rounded text-sm">
                            <Lightbulb className="w-4 h-4 inline mr-1" />
                            {type.aiTip}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label>Custom Plan Name (Optional)</Label>
                    <FieldTooltip content="Provide a custom name for this plan, or leave blank to use the default naming convention." />
                  </div>
                  <Input
                    placeholder="e.g., IT Disaster Recovery Plan 2025"
                    value={formData.plan_name}
                    onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                    data-testid="plan-name-input"
                  />
                </div>
          </div>
        )}

        {/* Step 1: Scope & Processes */}
        {wizardStep === 1 && (() => {
              const approvedProcesses = processes.filter(p => p.status === "approved");
              return (
              <div className="space-y-4">
                <AIInlineSuggestion
                  type="suggestion"
                  suggestion={`You have ${approvedProcesses.length} approved BIA(s). Select the critical processes to include in this plan.`}
                  onApply={() => {
                    setFormData({ ...formData, selected_processes: approvedProcesses.map(p => p.id) });
                  }}
                  actionLabel="Select All Approved"
                />

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label>Plan Scope</Label>
                    <FieldTooltip content="Define the scope and boundaries of this recovery plan." />
                  </div>
                  <Textarea
                    placeholder="Define the scope of this recovery plan (departments, systems, processes covered)..."
                    value={formData.scope}
                    onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                    rows={2}
                    data-testid="plan-scope-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Select Processes to Include</Label>
                  {approvedProcesses.length === 0 ? (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="no-approved-bias-empty">
                      <AlertTriangle className="w-4 h-4 text-amber-600 inline mr-2" />
                      <span className="text-sm text-amber-800">No approved BIAs yet</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                      {approvedProcesses.map((bia) => (
                        <div
                          key={bia.id}
                          onClick={() => toggleProcess(bia.id)}
                          data-testid={`plan-bia-tile-${bia.id}`}
                          className={`p-3 rounded-lg cursor-pointer border transition-all ${
                            formData.selected_processes.includes(bia.id)
                              ? "border-sky-700 bg-sky-50"
                              : "border-slate-200 hover:border-sky-300"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox checked={formData.selected_processes.includes(bia.id)} readOnly />
                            <div>
                              <p className="text-sm font-medium">{bia.process_name}</p>
                              <p className="text-xs text-slate-500">{bia.department}</p>
                            </div>
                            <Badge className="ml-auto" variant="secondary">
                              RTO {bia.calculated_rto_hours ?? bia.rto_hours ?? "—"}h
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">{formData.selected_processes.length} process(es) selected</p>
                  
                  {/* Phase D: Existing Plan Overlap Warning */}
                  {formData.selected_processes.length > 0 && plans.length > 0 && (() => {
                    const overlaps = plans.filter(p => {
                      const planProcessIds = (p.processes_included || []).map(pi => (typeof pi === "string" ? pi : pi.id));
                      return formData.selected_processes.some(sp => planProcessIds.includes(sp));
                    });
                    if (overlaps.length === 0) return null;
                    return (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mt-2" data-testid="existing-plan-overlap-warning">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-800">Existing Plan(s) Detected</p>
                            <p className="text-xs text-amber-700 mt-1">The selected processes overlap with {overlaps.length} existing plan(s):</p>
                            <ul className="text-xs text-amber-700 mt-1 space-y-1">
                              {overlaps.map(op => (
                                <li key={op.id} className="flex items-center gap-1">
                                  <span className="font-medium">{op.title || op.plan_type}</span>
                                  <Badge variant="outline" className="text-[10px] px-1">{op.status}</Badge>
                                </li>
                              ))}
                            </ul>
                            <p className="text-xs text-amber-600 mt-2 italic">You may update the existing plan instead of creating a duplicate.</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
          </div>
              );
            })()}

        {/* Step 2: Risk Integration */}
        {wizardStep === 2 && (
              <div className="space-y-4">
                <AIInlineSuggestion
                  type="info"
                  suggestion="Link relevant risk assessments to ensure your recovery plan addresses identified threats."
                />

                <div className="space-y-2">
                  <Label>Select Risks to Address</Label>
                  {riskAssessments.length === 0 ? (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <Info className="w-4 h-4 text-slate-500 inline mr-2" />
                      <span className="text-sm text-slate-600">No risk assessments found. The AI will generate general risk mitigation strategies.</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                      {riskAssessments.map((risk) => (
                        <div
                          key={risk.id}
                          onClick={() => toggleRisk(risk.id)}
                          className={`p-3 rounded-lg cursor-pointer border transition-all ${
                            formData.selected_risks.includes(risk.id)
                              ? "border-amber-500 bg-amber-50"
                              : "border-slate-200 hover:border-amber-300"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox checked={formData.selected_risks.includes(risk.id)} readOnly />
                            <div>
                              <p className="text-sm font-medium">{risk.title || "Untitled risk"}</p>
                              <p className="text-xs text-slate-500">{risk.category}</p>
                            </div>
                            {(() => {
                              const n = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v))) ? null : Number(v);
                              const a = n(risk.impact), b = n(risk.probability);
                              const rated = a !== null && b !== null;
                              return (
                                <Badge className="ml-auto" variant={rated && a * b >= 6 ? 'destructive' : 'secondary'}>
                                  Score: {rated ? (a * b) : "Not rated"}
                                </Badge>
                              );
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">{formData.selected_risks.length} risk(s) selected</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox
                      id="include_gaps"
                      checked={formData.include_gaps}
                      onCheckedChange={(checked) => setFormData({ ...formData, include_gaps: checked })}
                    />
                    <Label htmlFor="include_gaps" className="cursor-pointer">Include Gap Analysis</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox
                      id="include_recommendations"
                      checked={formData.include_recommendations}
                      onCheckedChange={(checked) => setFormData({ ...formData, include_recommendations: checked })}
                    />
                    <Label htmlFor="include_recommendations" className="cursor-pointer">Include AI Recommendations</Label>
                  </div>
                </div>
                
                {/* Recovery Team Selection from Stakeholders */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-amber-600" />
                      <Label>Recovery Team Members</Label>
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600">Smart Select</Badge>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowStakeholderPicker(!showStakeholderPicker)}
                      className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add from Stakeholders
                    </Button>
                  </div>
                  
                  {/* Stakeholder Picker */}
                  {showStakeholderPicker && stakeholders.length > 0 && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="text-xs text-amber-700 mb-2 font-medium">Select team members from Stakeholder Register:</div>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                        {stakeholders.map((s) => {
                          const isSelected = formData.contact_stakeholder_ids?.includes(s.id) || 
                                           formData.recovery_teams?.includes(s.name);
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  // Remove
                                  setFormData(prev => ({
                                    ...prev,
                                    contact_stakeholder_ids: prev.contact_stakeholder_ids?.filter(id => id !== s.id) || [],
                                    recovery_teams: prev.recovery_teams?.filter(name => name !== s.name) || []
                                  }));
                                } else {
                                  // Add
                                  setFormData(prev => ({
                                    ...prev,
                                    contact_stakeholder_ids: [...(prev.contact_stakeholder_ids || []), s.id],
                                    recovery_teams: [...prev.recovery_teams, s.name]
                                  }));
                                  toast.success(`Added ${s.name} to recovery team`);
                                }
                              }}
                              className={`p-2 rounded-lg text-left flex items-center gap-2 border transition-all ${
                                isSelected 
                                  ? "border-amber-500 bg-amber-100" 
                                  : "border-amber-200 bg-white hover:border-amber-400"
                              }`}
                            >
                              <div className="w-8 h-8 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                                {s.name?.charAt(0)?.toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{s.name}</p>
                                <p className="text-xs text-slate-500 truncate">{s.type || "Internal"}</p>
                              </div>
                              {isSelected && <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {stakeholders.length === 0 && (
                    <div className="p-2 bg-slate-50 rounded text-xs text-slate-500 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      No stakeholders in register. Add team members manually below.
                    </div>
                  )}
                  
                  {/* Manual Team Entry + Selected Teams Display */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add team member name..."
                      value={tempTeam}
                      onChange={(e) => setTempTeam(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTeam())}
                      className="flex-1"
                      data-testid="add-team-input"
                    />
                    <Button type="button" variant="outline" onClick={addTeam} disabled={!tempTeam.trim()}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {formData.recovery_teams?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.recovery_teams.map((team, index) => {
                        const isLinkedStakeholder = formData.contact_stakeholder_ids?.some(id => 
                          stakeholders.find(s => s.id === id)?.name === team
                        );
                        return (
                          <Badge 
                            key={index} 
                            variant="outline" 
                            className={`gap-1 ${isLinkedStakeholder ? "bg-amber-50 border-amber-300" : "bg-slate-50"}`}
                          >
                            {isLinkedStakeholder && <Users className="w-3 h-3 text-amber-600" />}
                            {team}
                            <button
                              type="button"
                              onClick={() => removeTeam(index)}
                              className="ml-1 hover:text-red-500"
                            >
                              ×
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-500">
                    {formData.recovery_teams?.length || 0} team member(s) assigned
                    {formData.contact_stakeholder_ids?.length > 0 && (
                      <span className="text-amber-600"> ({formData.contact_stakeholder_ids.length} linked to stakeholder register)</span>
                    )}
                  </p>
                </div>
          </div>
        )}

        {/* Step 3: Recovery Tasks */}
        {wizardStep === 3 && (
              <div className="space-y-4">
                {!taskMode ? (
                  <div className="space-y-4">
                    <AIInlineSuggestion
                      type="tip"
                      suggestion="Define the step-by-step recovery tasks for this plan. Choose AI-generated tasks customized to your context, or start from a blank template."
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={handleAIGenerateTasks}
                        disabled={aiTasksLoading}
                        className="p-6 rounded-xl border-2 border-sky-200 hover:border-sky-400 bg-sky-50 hover:bg-sky-100 transition-all text-left"
                        data-testid="ai-generate-tasks-btn"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-sky-200 rounded-lg">
                            {aiTasksLoading ? <Loader2 className="w-5 h-5 text-sky-700 animate-spin" /> : <Sparkles className="w-5 h-5 text-sky-700" />}
                          </div>
                          <span className="font-semibold text-sky-900">AI Generate Tasks</span>
                        </div>
                        <p className="text-sm text-sky-700">
                          Auto-generate 10-12 structured tasks based on your plan type, selected processes, and risks. Review and edit.
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={handleStartBlankTemplate}
                        className="p-6 rounded-xl border-2 border-slate-200 hover:border-slate-400 bg-slate-50 hover:bg-slate-100 transition-all text-left"
                        data-testid="blank-template-btn"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-slate-200 rounded-lg">
                            <ClipboardCheck className="w-5 h-5 text-slate-700" />
                          </div>
                          <span className="font-semibold text-slate-900">Blank Template</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Start with an empty task table. Add tasks manually row by row with AI suggestions available on the side.
                        </p>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ListOrdered className="w-4 h-4 text-sky-700" />
                        <Label className="text-base font-semibold">Recovery Task Checklist</Label>
                        <Badge variant="outline" className="text-xs">{recoveryTasks.length} tasks</Badge>
                        {taskMode === "ai" && <Badge className="bg-sky-100 text-sky-700 text-xs">AI Generated</Badge>}
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => { setTaskMode(null); setRecoveryTasks([]); setEditingTaskIdx(null); }}>
                          Reset
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={handleAIGenerateTasks} disabled={aiTasksLoading}>
                          {aiTasksLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                          Regenerate
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={addTask} data-testid="add-task-btn">
                          <Plus className="w-3 h-3 mr-1" /> Add Task
                        </Button>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-auto max-h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="w-[70px] text-xs">Task ID</TableHead>
                            <TableHead className="text-xs min-w-[180px]">Actual Task</TableHead>
                            <TableHead className="text-xs min-w-[120px]">Task Owner</TableHead>
                            <TableHead className="w-[80px] text-xs">Pred.</TableHead>
                            <TableHead className="text-xs min-w-[90px]">Duration</TableHead>
                            <TableHead className="text-xs min-w-[80px]">Start</TableHead>
                            <TableHead className="text-xs min-w-[80px]">Complete</TableHead>
                            <TableHead className="text-xs min-w-[120px]">Notes</TableHead>
                            <TableHead className="w-[40px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {recoveryTasks.map((task, idx) => (
                            <TableRow
                              key={idx}
                              className={`cursor-pointer ${editingTaskIdx === idx ? "bg-sky-50" : "hover:bg-slate-50"}`}
                              onClick={() => setEditingTaskIdx(idx)}
                            >
                              <TableCell className="font-mono text-xs font-medium py-1">
                                {editingTaskIdx === idx ? (
                                  <Input value={task.task_id} onChange={(e) => updateTask(idx, "task_id", e.target.value)} className="h-7 text-xs w-16" onClick={(e) => e.stopPropagation()} />
                                ) : task.task_id}
                              </TableCell>
                              <TableCell className="py-1">
                                {editingTaskIdx === idx ? (
                                  <Input value={task.actual_task} onChange={(e) => updateTask(idx, "actual_task", e.target.value)} className="h-7 text-xs" placeholder="Task description..." onClick={(e) => e.stopPropagation()} />
                                ) : <span className="text-xs">{task.actual_task}</span>}
                              </TableCell>
                              <TableCell className="py-1">
                                {editingTaskIdx === idx ? (
                                  <Input value={task.task_owner} onChange={(e) => updateTask(idx, "task_owner", e.target.value)} className="h-7 text-xs" placeholder="Owner..." onClick={(e) => e.stopPropagation()} />
                                ) : <span className="text-xs">{task.task_owner}</span>}
                              </TableCell>
                              <TableCell className="py-1">
                                {editingTaskIdx === idx ? (
                                  <Input value={task.predecessor_task_id} onChange={(e) => updateTask(idx, "predecessor_task_id", e.target.value)} className="h-7 text-xs w-16" onClick={(e) => e.stopPropagation()} />
                                ) : <span className="text-xs font-mono text-slate-500">{task.predecessor_task_id || "-"}</span>}
                              </TableCell>
                              <TableCell className="py-1">
                                {editingTaskIdx === idx ? (
                                  <Input value={task.estimated_duration} onChange={(e) => updateTask(idx, "estimated_duration", e.target.value)} className="h-7 text-xs" placeholder="e.g. 2 hrs" onClick={(e) => e.stopPropagation()} />
                                ) : <span className="text-xs">{task.estimated_duration}</span>}
                              </TableCell>
                              <TableCell className="py-1">
                                {editingTaskIdx === idx ? (
                                  <Input value={task.start_time} onChange={(e) => updateTask(idx, "start_time", e.target.value)} className="h-7 text-xs" placeholder="T+0" onClick={(e) => e.stopPropagation()} />
                                ) : <span className="text-xs font-mono">{task.start_time}</span>}
                              </TableCell>
                              <TableCell className="py-1">
                                {editingTaskIdx === idx ? (
                                  <Input value={task.completion_time} onChange={(e) => updateTask(idx, "completion_time", e.target.value)} className="h-7 text-xs" placeholder="T+2h" onClick={(e) => e.stopPropagation()} />
                                ) : <span className="text-xs font-mono">{task.completion_time}</span>}
                              </TableCell>
                              <TableCell className="py-1">
                                {editingTaskIdx === idx ? (
                                  <Input value={task.notes} onChange={(e) => updateTask(idx, "notes", e.target.value)} className="h-7 text-xs" placeholder="Notes..." onClick={(e) => e.stopPropagation()} />
                                ) : <span className="text-xs text-slate-500">{task.notes}</span>}
                              </TableCell>
                              <TableCell className="py-1">
                                <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeTask(idx); }} className="h-6 w-6 p-0 text-red-400 hover:text-red-600">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <p className="text-xs text-slate-500">Click any row to edit inline. Tasks will be saved with the generated plan.</p>
                  </div>
                )}
          </div>
        )}

        {/* Step 4: Review */}
        {wizardStep === 4 && (
              <div className="space-y-4">
                <AIInlineSuggestion
                  type="info"
                  suggestion="Review your configuration. The AI will generate a comprehensive recovery plan based on your selections."
                />
                
                <div className="grid gap-4">
                  <StepSummaryCard
                    title="Plan Configuration"
                    icon={FileText}
                    items={[
                      { label: "Plan Type", value: getPlanTypeConfig(formData.plan_type).label },
                      { label: "Custom Name", value: formData.plan_name || "Default naming" },
                      { label: "Scope", value: formData.scope || "Not specified" },
                    ]}
                    onEdit={() => setWizardStep(0)}
                  />
                  
                  <StepSummaryCard
                    title="Processes & Risks"
                    icon={Target}
                    items={[
                      { label: "Processes Included", value: formData.selected_processes.length > 0 ? `${formData.selected_processes.length} selected` : "All available" },
                      { label: "Risks Linked", value: formData.selected_risks.length > 0 ? `${formData.selected_risks.length} selected` : "All available" },
                    ]}
                    onEdit={() => setWizardStep(1)}
                  />
                  
                  <StepSummaryCard
                    title="AI Options"
                    icon={Sparkles}
                    items={[
                      { label: "Gap Analysis", value: formData.include_gaps ? "Included" : "Not included" },
                      { label: "AI Recommendations", value: formData.include_recommendations ? "Included" : "Not included" },
                    ]}
                    onEdit={() => setWizardStep(2)}
                  />
                  
                  <StepSummaryCard
                    title="Recovery Tasks"
                    icon={ListOrdered}
                    items={[
                      { label: "Tasks Defined", value: recoveryTasks.length > 0 ? `${recoveryTasks.length} tasks` : "None (will use AI narrative only)" },
                      { label: "Source", value: taskMode === "ai" ? "AI Generated" : taskMode === "blank" ? "Manual Entry" : "Not configured" },
                    ]}
                    onEdit={() => setWizardStep(3)}
                  />
                </div>
          </div>
        )}
      </StepDialog>

      {/* View Plan Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>{selectedPlan?.title}</DialogTitle>
                <DialogDescription>
                  Version {selectedPlan?.version} • {selectedPlan?.processes_included?.length || 0} processes included
                </DialogDescription>
              </div>
              <Badge className={STATUS_COLORS[selectedPlan?.status] || STATUS_COLORS.draft}>
                {selectedPlan?.status?.replace("_", " ")}
              </Badge>
            </div>
          </DialogHeader>

          {selectedPlan && (
            <Tabs value={planDetailTab} onValueChange={setPlanDetailTab} className="mt-4">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="overview" data-testid="plan-tab-overview">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Overview
                </TabsTrigger>
                <TabsTrigger value="workarounds" data-testid="plan-tab-workarounds" className="relative">
                  <Wrench className="w-3.5 h-3.5 mr-1.5" /> Manual Workarounds & SOPs
                  <Badge className="ml-1.5 text-[9px] bg-sky-500 text-white px-1 py-0">NEW</Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 mt-4">
              {/* Metadata */}
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Generated</p>
                  <p className="font-medium">{new Date(selectedPlan.generated_at).toLocaleString()}</p>
                </div>
                {selectedPlan.approved_at && (
                  <div>
                    <p className="text-sm text-slate-500">Approved</p>
                    <p className="font-medium text-emerald-600">
                      {new Date(selectedPlan.approved_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Optimization Queue in Plan */}
              {selectedPlan.optimization_queue?.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader className="py-3">
                    <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="w-4 h-4" />
                      Optimization Queue ({selectedPlan.optimization_queue.length} items)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedPlan.optimization_queue.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="p-2 bg-amber-50 rounded text-sm">
                          <span className="font-medium">{item.process_name}:</span> {item.description}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* RP1a: BIAs included — name every entry; flag missing ones */}
              {selectedPlan.processes_included?.length > 0 && (
                <div className="p-4 bg-white rounded-lg border" data-testid="plan-processes-included">
                  <p className="text-sm font-semibold text-slate-700 mb-2">BIAs Included ({selectedPlan.processes_included.length})</p>
                  <ul className="space-y-1">
                    {selectedPlan.processes_included.map((pi, idx) => (
                      <li key={(typeof pi === "string" ? pi : pi.id) || idx} className="flex items-center gap-2 text-sm" data-testid={`plan-bia-entry-${idx}`}>
                        {pi.missing ? (
                          <span className="text-amber-600 italic">BIA no longer exists</span>
                        ) : (
                          <>
                            <span className="font-medium text-slate-800">{pi.name}</span>
                            {(pi.rto !== null && pi.rto !== undefined) && (
                              <Badge variant="secondary" className="text-[10px]">RTO {pi.rto}h</Badge>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Plan Content */}
              <div className="p-6 bg-white rounded-lg border overflow-auto max-h-[500px]">
                <FormattedContent content={selectedPlan.content} />
              </div>

              {/* Recovery Tasks Table & Gantt */}
              {selectedPlan.recovery_tasks?.length > 0 && (
                <Card className="p-0 overflow-hidden" data-testid="plan-recovery-tasks-table">
                  <div className="px-4 py-3 bg-slate-50 border-b flex items-center gap-2">
                    <ListOrdered className="w-4 h-4 text-sky-700" />
                    <h4 className="font-semibold text-sm">Recovery Task Checklist</h4>
                    <Badge variant="outline" className="text-xs">{selectedPlan.recovery_tasks.length} tasks</Badge>
                    <div className="ml-auto flex gap-1">
                      <Button
                        variant={taskViewMode === "table" ? "default" : "ghost"}
                        size="sm"
                        className="h-7 px-2.5 text-xs"
                        onClick={() => setTaskViewMode("table")}
                        data-testid="task-view-table-btn"
                      >
                        <ListOrdered className="w-3 h-3 mr-1" /> Table
                      </Button>
                      <Button
                        variant={taskViewMode === "gantt" ? "default" : "ghost"}
                        size="sm"
                        className="h-7 px-2.5 text-xs"
                        onClick={() => setTaskViewMode("gantt")}
                        data-testid="task-view-gantt-btn"
                      >
                        <BarChart3 className="w-3 h-3 mr-1" /> Gantt
                      </Button>
                    </div>
                  </div>

                  {taskViewMode === "table" ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/50">
                            <TableHead className="text-xs w-[70px]">Task ID</TableHead>
                            <TableHead className="text-xs">Actual Task</TableHead>
                            <TableHead className="text-xs">Task Owner</TableHead>
                            <TableHead className="text-xs w-[70px]">Pred.</TableHead>
                            <TableHead className="text-xs">Duration</TableHead>
                            <TableHead className="text-xs">Start</TableHead>
                            <TableHead className="text-xs">Complete</TableHead>
                            <TableHead className="text-xs">Notes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedPlan.recovery_tasks.map((task, idx) => (
                            <TableRow key={idx} className="hover:bg-slate-50">
                              <TableCell className="font-mono text-xs font-medium py-1.5">{task.task_id}</TableCell>
                              <TableCell className="text-xs py-1.5">{task.actual_task}</TableCell>
                              <TableCell className="text-xs py-1.5">{task.task_owner}</TableCell>
                              <TableCell className="text-xs font-mono text-slate-500 py-1.5">{task.predecessor_task_id || "-"}</TableCell>
                              <TableCell className="text-xs py-1.5">{task.estimated_duration}</TableCell>
                              <TableCell className="text-xs font-mono py-1.5">{task.start_time}</TableCell>
                              <TableCell className="text-xs font-mono py-1.5">{task.completion_time}</TableCell>
                              <TableCell className="text-xs text-slate-500 py-1.5">{task.notes}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="p-4">
                      <RecoveryGanttChart tasks={selectedPlan.recovery_tasks} />
                    </div>
                  )}
                </Card>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2">
                {selectedPlan.status === "draft" && (
                  <Button onClick={() => handleApprovePlan(selectedPlan.id)} className="bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Approve & Sign Off
                  </Button>
                )}
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
              </TabsContent>

              <TabsContent value="workarounds" className="mt-4">
                <ManualWorkaroundsTab
                  planId={selectedPlan.id}
                  orgId={user?.org_id}
                  subIndustry={selectedPlan.sub_industry || ""}
                  departmentName={selectedPlan.department_name || selectedPlan.title || "General"}
                  serviceLine={selectedPlan.service_line || ""}
                  systemName={selectedPlan.system_name || ""}
                  rto={selectedPlan.rto || "4h"}
                  criticality={selectedPlan.criticality || "critical"}
                />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
